import AppPaths from '@globals/paths'
import { KUDOS } from '@globals/paths/useDocumentTitle'
import { T, useT } from '@transifex/react'
import { useEffect } from 'react'
import ErrorPageLayout from './ErrorPageLayout'
import { isGoogleTranslateEnabled } from './utils'

export const IMPORT_ERROR_BOUNDARY_UI_KEY = 'moduleImportError-storage'
const isLoadingModuleFailed = (message: string) =>
  /(Loading chunk [\d]+ failed|Loading CSS chunk [\d]+ failed|Failed to fetch dynamically imported module: [^]*|Importing a module script failed|'text\/html' is not a valid JavaScript MIME type)/.test(
    message,
  )

type ErrorBoundaryUIProps = {
  error: Error | null
  resetError: () => void
}

export const ErrorBoundaryUI: React.FC<ErrorBoundaryUIProps> = ({ error, resetError }) => {
  const t = useT()
  const hasPageReloaded = window.localStorage.getItem(IMPORT_ERROR_BOUNDARY_UI_KEY) === 'true'
  useEffect(() => {
    document.title = t('{kudos} - Error!', { kudos: KUDOS })
  }, [])

  const isLoadingModuleFailedError =
    error && error?.message ? isLoadingModuleFailed(error.message) : false

  useEffect(() => {
    if (isLoadingModuleFailedError && !hasPageReloaded) {
      window.localStorage.setItem(IMPORT_ERROR_BOUNDARY_UI_KEY, 'true')
      window.location.reload()
    }
  }, [hasPageReloaded, isLoadingModuleFailedError])

  useEffect(() => {
    if (hasPageReloaded) {
      window.onbeforeunload = () => {
        // Removes error boundary key from localstorage if user refreshes page manually, closes tab/window,
        // or clicks Reload Page.
        window.localStorage.removeItem(IMPORT_ERROR_BOUNDARY_UI_KEY)
      }
    }
  }, [hasPageReloaded])
  const onClick = () => {
    if (isLoadingModuleFailedError) {
      window.location.reload()
    } else {
      resetError()
    }
  }

  if (isGoogleTranslateEnabled()) {
    return (
      <ErrorPageLayout
        // Note using window.location.replace instead of navigator in case they are on the home page.
        // If they are on the home page we want their page to at least refresh

        onClick={() => window.location.replace(AppPaths.tenant.home())}
        title={<T _str="Turn off browser translation" />}
        description={
          <T _str="It looks like your browser is translating this page, which can cause unexpected behaviour in Kudos. Please turn off your browser’s translate feature and refresh the page." />
        }
        buttonText={<T _str="Back to home" />}
      />
    )
  }
  return <ErrorPageLayout onClick={onClick} title={<T _str="An error has occurred" />} />
}

export default ErrorBoundaryUI
